import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Camp, Sectiune, BaraSalvare } from '@/ui/form'
import { PartenerPicker } from '@/ui/partner-picker'
import { ETAPE_REZERVARE, MONEDE, TIPURI_SERVICIU, azi } from '@/lib/domeniu'

/** Rezervarea „scurta": datele care fac dosarul sa existe. Pachetele complicate (camere,
 *  turisti, grupuri de facturare) raman pe wizardul din aplicatia veche. */
export function RezervareNoua() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [r, setR] = useState({
    number: '', partner: '', booking_customer: '', status: 'Optiune',
    service: '', service_type: 'SEJUR_CHARTER', destination: '', hotel: '',
    begin_date: '', end_date: '', adults: '2', children: '0',
    total_value: '', cost_value: '', currency: 'EUR', suppliers: '',
    client_phone: '', client_email: '', responsible: '', remarks: '',
  })
  const [partnerId, setPartnerId] = useState<number | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)
  const s = (k: keyof typeof r) => (v: string) => setR((x) => ({ ...x, [k]: v }))
  const nr = (v: string) => Number(String(v).replace(',', '.')) || 0

  const salveaza = useMutation({
    mutationFn: () => {
      if (!r.partner.trim()) throw new ApiError(400, 'Clientul e obligatoriu')
      if (!r.begin_date) throw new ApiError(400, 'Data plecarii e obligatorie')
      return api.post<{ id?: number; reservation?: { id: number } }>('/reservations/', {
        number: r.number || undefined,
        partner_id: partnerId ?? undefined,
        partner: r.partner,
        booking_customer: r.booking_customer || r.partner,
        status: r.status,
        issue_date: azi(),
        begin_date: r.begin_date,
        end_date: r.end_date || r.begin_date,
        destination: r.destination || undefined,
        hotel: r.hotel || undefined,
        service: r.service || undefined,
        adults: Number(r.adults) || 0,
        children: Number(r.children) || 0,
        total_value: nr(r.total_value),
        cost_value: nr(r.cost_value),
        currency: r.currency,
        suppliers: r.suppliers || undefined,
        client_phone: r.client_phone || undefined,
        client_email: r.client_email || undefined,
        responsible: r.responsible || undefined,
        remarks: r.remarks || undefined,
      })
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['rezervari'] })
      const id = res?.id ?? res?.reservation?.id
      nav(id ? `/rezervari/${id}` : '/rezervari')
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  return (
    <>
      <PageHeader title="Rezervare noua"
        count="formular scurt — pentru camere, turisti si facturare pe grupuri foloseste wizardul" />
      <div className="px-8 pb-0">
        <Sectiune titlu="Client" descriere="Titularul dosarului.">
          <PartenerPicker valoare={r.partner} seteaza={s('partner')} obligatoriu
            alege={(p) => {
              setPartnerId(p.id)
              setR((x) => ({ ...x, client_phone: p.phone ?? x.client_phone, client_email: p.email ?? x.client_email }))
            }} />
          <Camp eticheta="Titular calatorie" valoare={r.booking_customer} seteaza={s('booking_customer')}
            indiciu="gol = acelasi cu clientul" />
          <Camp eticheta="Telefon" tip="tel" valoare={r.client_phone} seteaza={s('client_phone')} />
          <Camp eticheta="Email" tip="email" valoare={r.client_email} seteaza={s('client_email')} />
        </Sectiune>

        <Sectiune titlu="Calatoria">
          <Camp eticheta="Tip serviciu" tip="select" valoare={r.service_type} seteaza={s('service_type')}
            optiuni={TIPURI_SERVICIU} />
          <Camp eticheta="Etapa" tip="select" valoare={r.status} seteaza={s('status')} optiuni={ETAPE_REZERVARE} />
          <Camp eticheta="Destinatie" valoare={r.destination} seteaza={s('destination')} />
          <Camp eticheta="Hotel" valoare={r.hotel} seteaza={s('hotel')} />
          <Camp eticheta="Descriere serviciu" valoare={r.service} seteaza={s('service')} latime="plin" />
          <Camp eticheta="Plecare" tip="date" valoare={r.begin_date} seteaza={s('begin_date')} obligatoriu />
          <Camp eticheta="Intoarcere" tip="date" valoare={r.end_date} seteaza={s('end_date')} />
          <Camp eticheta="Adulti" tip="number" valoare={r.adults} seteaza={s('adults')} />
          <Camp eticheta="Copii" tip="number" valoare={r.children} seteaza={s('children')} />
        </Sectiune>

        <Sectiune titlu="Bani si organizare">
          <Camp eticheta="Pret de vanzare" valoare={r.total_value} seteaza={s('total_value')} />
          <Camp eticheta="Cost furnizor" valoare={r.cost_value} seteaza={s('cost_value')} />
          <Camp eticheta="Moneda" tip="select" valoare={r.currency} seteaza={s('currency')} optiuni={MONEDE} />
          <Camp eticheta="Furnizor" valoare={r.suppliers} seteaza={s('suppliers')} />
          <Camp eticheta="Numar rezervare" valoare={r.number} seteaza={s('number')}
            indiciu="gol = numerotare automata" />
          <Camp eticheta="Agent" valoare={r.responsible} seteaza={s('responsible')} />
          <Camp eticheta="Observatii" tip="textarea" valoare={r.remarks} seteaza={s('remarks')} latime="plin" />
        </Sectiune>

        <BaraSalvare salveaza={() => { setEroare(null); salveaza.mutate() }}
          seSalveaza={salveaza.isPending} eroare={eroare} eticheta="Salveaza rezervarea" />
      </div>
    </>
  )
}
